import {
  EventType,
  SyncJobEventMessage,
  SyncJobEventType,
  WebSocketContext,
} from 'contexts/WebSocketContext';
import { useObservable, useObservableState } from 'observable-hooks';
import { useCallback, useContext, useState } from 'react';
import { merge, Subject } from 'rxjs';
import { filter, map, scan } from 'rxjs/operators';
import { ProjectDrivesActiveSyncs } from 'utils/ProjectDrivesActiveSyncs';

type ClientEventMessage = {
  event: 'CLEAR';
};

const eventIsSyncJobEvent = (message: {
  type?: string;
  event: string;
}): message is SyncJobEventMessage =>
  message.type === EventType.FileEvent &&
  (message.event === SyncJobEventType.UploadError ||
    message.event === SyncJobEventType.PendingUpload ||
    message.event === SyncJobEventType.UploadComplete);

const useAdornerState = () => {
  const { receive } = useContext(WebSocketContext);

  const [clientSubject] = useState(new Subject<ClientEventMessage>());

  const clearAdornerState = useCallback(() => {
    clientSubject.next({ event: 'CLEAR' });
  }, [clientSubject]);

  const syncAdornerState$ = useObservable(() =>
    merge(clientSubject, receive().pipe(filter(eventIsSyncJobEvent))).pipe(
      scan((activeSyncs, message) => {
        if (eventIsSyncJobEvent(message)) {
          return activeSyncs.receivedFileEvent(message.driveid, message.event);
        }

        if (message.event === 'CLEAR') {
          return new ProjectDrivesActiveSyncs();
        }

        return activeSyncs;
      }, new ProjectDrivesActiveSyncs()),
      map((activeSyncs) => activeSyncs.adornerState())
    )
  );

  const syncAdornerState = useObservableState(
    syncAdornerState$,
    new ProjectDrivesActiveSyncs().adornerState()
  );

  return {
    clearAdornerState,
    syncAdornerState,
  };
};

export default useAdornerState;
